import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Key, Users, MessageSquare,
  Laptop, FileText, Calendar, Target, ChevronRight, X, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingLayout } from '../components/marketing/MarketingLayout';

const requiredConnectors = [
  { name: 'Identity Provider', examples: 'Okta, Entra ID, or Google Workspace', icon: Key, required: true },
  { name: 'HRIS', examples: 'BambooHR, Rippling, Workday', icon: Users, required: true },
  { name: 'Chat Platform', examples: 'Slack or Microsoft Teams', icon: MessageSquare, required: true },
  { name: 'MDM (Optional)', examples: 'Intune, Jamf', icon: Laptop, required: false },
  { name: 'Ticketing (Optional)', examples: 'Jira, Zendesk', icon: FileText, required: false }
];

const week1Activities = [
  'Connect identity provider and HRIS',
  'Connect chat platform for intake',
  'Baseline current JML metrics (time to productivity, offboard completion)',
  'Identify top 10 JML cases from past 30 days',
  'Map existing approval workflows',
  'Configure policy rules (who approves what)'
];

const week2Activities = [
  'Create action bundles for standard onboarding',
  'Route approvals through Slack/Teams',
  'Execute first cases with verification',
  'Measure: time to first response, time to resolution',
  'Iterate on policy rules based on feedback',
  'Enable auto-execute for low-risk actions'
];

const successMetrics = [
  { metric: 'Time to productivity', baseline: '3-5 days', target: '< 4 hours' },
  { metric: 'Offboard completion rate', baseline: '60-70%', target: '99%' },
  { metric: 'Auto-resolution rate', baseline: '0%', target: '50%+' },
  { metric: 'Time to first response', baseline: '4-24 hours', target: '< 15 minutes' },
  { metric: 'Cost per JML case', baseline: 'Manual estimate', target: 'Tracked' }
];

const timeline = [
  { day: '1-2', activity: 'Connector setup', owner: 'Vedaya', description: 'Connect IdP, HRIS, and chat platform' },
  { day: '3-4', activity: 'Policy configuration', owner: 'Joint', description: 'Define approval rules and routing' },
  { day: '5', activity: 'First evidence review', owner: 'Joint', description: 'Review baseline data and automation candidates' },
  { day: '6-7', activity: 'Action bundle creation', owner: 'Vedaya', description: 'Build onboarding and offboarding workflows' },
  { day: '8-10', activity: 'Execute pilot cases', owner: 'Joint', description: 'Run cases with real employees' },
  { day: '11-12', activity: 'Iterate and optimize', owner: 'Joint', description: 'Refine based on feedback' },
  { day: '13-14', activity: 'Pilot report and review', owner: 'Joint', description: 'Metrics comparison and recommendations' }
];

const included = [
  'Up to 2 identity connectors',
  'HRIS integration',
  'Chat platform integration',
  'Up to 50 JML cases',
  'Standard onboarding and offboarding workflows',
  'Dedicated implementation support',
  'Weekly check-in calls',
  'Final pilot report with metrics'
];

const notIncluded = [
  'Custom connector development',
  'Enterprise SSO configuration',
  'Data migration from legacy systems',
  'On-premise deployment',
  'Custom reporting beyond pilot metrics'
];

const faqs = [
  {
    question: 'What happens after the pilot?',
    answer: 'At the end of 14 days, you receive a pilot report comparing before/after metrics. If results meet expectations, we transition to a full deployment. If not, no commitment required.'
  },
  {
    question: 'What if we need more time?',
    answer: 'The 14-day timeline is designed for focused execution. Extensions are possible but may require additional scoping to maintain momentum.'
  },
  {
    question: 'What IT resources do we need to provide?',
    answer: 'Admin access to identity provider and HRIS (read-only initially, write for week 2). An IT sponsor for approvals and a point person for testing. Typically 2-4 hours per week of involvement.'
  },
  {
    question: 'Can we customize the workflows?',
    answer: 'The pilot uses standard workflows optimized for quick time-to-value. Custom workflows are available in full deployment but not included in pilot scope.'
  }
];

export const Pilot = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen" data-testid="pilot-page">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Pilot Program</span>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
                  14-Day Pilot Program
                </h1>
                <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed mb-6">
                  Fixed scope. Clear metrics. No commitment until you see results.
                  Connect your systems, run real cases, and measure the impact.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild size="default">
                    <Link to="/demo/request">
                      Start Your Pilot
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button asChild size="default" variant="outline">
                    <Link to="/pricing">View Pricing</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Required Connectors */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Prerequisites</h2>
            <p className="text-muted-foreground mb-8">What you need to connect for the pilot.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {requiredConnectors.map((conn, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-border">
                  <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <conn.icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-foreground">{conn.name}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                        conn.required ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'
                      }`}>
                        {conn.required ? 'Required' : 'Optional'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{conn.examples}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pilot Phases */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Pilot Phases</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-secondary/30 rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <span className="font-semibold">1</span>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">Week 1: Evidence Gathering</h3>
                    <p className="text-sm text-muted-foreground">Read-only mode</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4">
                  {week1Activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-foreground">{activity}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Deliverable:</span> Evidence dashboard showing current state and automation candidates
                  </p>
                </div>
              </div>

              <div className="bg-secondary/30 rounded-lg border border-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <span className="font-semibold">2</span>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">Week 2: Action Bundles</h3>
                    <p className="text-sm text-muted-foreground">Propose and execute</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4">
                  {week2Activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground">{activity}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Deliverable:</span> Pilot report with metrics comparison (before vs after)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Metrics */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Target className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Success Metrics</h2>
            </div>
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <div className="grid grid-cols-3 bg-secondary/50 border-b border-border px-4 py-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Metric</div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Typical Baseline</div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pilot Target</div>
              </div>
              {successMetrics.map((m, idx) => (
                <div key={idx} className="grid grid-cols-3 px-4 py-3 border-b border-border last:border-0">
                  <div className="text-sm font-medium text-foreground">{m.metric}</div>
                  <div className="text-sm text-muted-foreground">{m.baseline}</div>
                  <div className="text-sm font-medium text-primary">{m.target}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Calendar className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Implementation Timeline</h2>
            </div>
            <div className="space-y-3">
              {timeline.map((t, idx) => (
                <div key={idx} className="flex items-start gap-4 bg-secondary/30 rounded-lg border border-border p-4">
                  <div className="w-16 shrink-0">
                    <div className="text-sm font-semibold text-foreground">Day {t.day}</div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm font-semibold text-foreground">{t.activity}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                        t.owner === 'Vedaya' ? 'bg-primary/10 text-primary' :
                        'bg-secondary text-muted-foreground'
                      }`}>
                        {t.owner}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{t.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Scope */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Pilot Scope</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-border p-6">
                <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Included
                </h3>
                <ul className="space-y-2">
                  {included.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-lg border border-border p-6">
                <h3 className="text-base font-semibold text-muted-foreground mb-4 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  Not Included
                </h3>
                <ul className="space-y-2">
                  {notIncluded.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <X className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2">{faq.question}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <Clock className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-foreground mb-4">Ready to Start?</h2>
            <p className="text-muted-foreground mb-8">
              14 days from connector setup to measurable results.
              No long-term commitment until you see the impact.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Pilot
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/control-tower">Explore Control Tower</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default Pilot;
